import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
  FormControl,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import { ChevronRightIcon, ChevronLeftIcon } from "@heroicons/react/24/solid";
import { useLocation, useNavigate } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import debounce from "lodash.debounce";
import SuccessModal from "./SuccessModal";
import {
  assignMultipleSurveys,
  getUsers,
  getUsersByGroupId,
  listAllGroups,
} from "../../../services/user.service";

const AssignCoach = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [coaches, setCoaches] = useState([]);
  console.log("coaches", coaches);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [roles, setRoles] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [prevCursors, setPrevCursors] = useState([]); // for back navigation
  const [limit, setLimit] = useState(10);
  const [filter, setFilter] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);
  const [assigningCoachId, setAssigningCoachId] = useState(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [prevStack, setPrevStack] = useState([]);

  const getQueryParams = (query) => new URLSearchParams(query).getAll("ids");
  const selectedIds = getQueryParams(location.search);

  useEffect(() => {
    (async () => {
      try {
        const list = await listAllGroups(1, 25);
        const filtered = list.filter((g) => g.name.toLowerCase() !== "admins");
        setRoles(filtered);
        const coachGroup = filtered.find(
          (g) => g.name.toLowerCase() === "coachs"
        );
        setSelectedRole(coachGroup ?? null);
      } catch (err) {
        console.error("roles fetch error", err);
      }
    })();
  }, []);

  // Fetch Users
  const fetchUsers = useCallback(
    async (term = "") => {
      if (!selectedRole) return;
      setLoading(true);
      try {
        const res = await getUsers({
          cursor: cursor ?? undefined,
          limit,
          search: term || undefined,
          role: selectedRole.name,
          emailVerified: filter || undefined,
          fields: "id,firstName,email,keycloakUserId,ville",
        });

        setCoaches(res?.users || []);
        setNextCursor(res?.nextCursor ?? null);
      } catch (err) {
        console.error("users fetch error", err);
      } finally {
        setLoading(false);
      }
    },
    [selectedRole, cursor, limit, filter]
  );

  // Call fetch on dependencies
  useEffect(() => {
    fetchUsers(searchTerm);
  }, [fetchUsers, searchTerm]);

  // Pagination handlers
  const handleNextPage = () => {
    if (nextCursor) {
      setPrevCursors((prev) => [...prev, cursor]);
      setCursor(nextCursor);
    }
  };

  const handlePrevPage = () => {
    const prev = [...prevCursors];
    const last = prev.pop();
    setPrevCursors(prev);
    setCursor(last ?? null);
  };

  const handleAssignCoach = async (coach) => {
    if (!coach || selectedIds.length === 0) return;

    setIsAssigning(true);
    setAssigningCoachId(coach.keycloakUserId);
    console.log("coach.id", coach.keycloakUserId);

    try {
      await assignMultipleSurveys(coach.keycloakUserId, selectedIds);
      setSelectedCoach(coach);
      setSuccessModalOpen(true);
    } catch (error) {
      console.error("Error assigning surveys:", error);
      alert("Failed to assign surveys. Please try again.");
    } finally {
      setIsAssigning(false);
      setAssigningCoachId(null);
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
    setSelectedCoach(null);
    navigate("../liste-candidatures", { replace: true });
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
      setCursor(null);
      setPrevCursors([]);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    debouncedSearch(event.target.value);
  };

  const changeLimit = (e) => {
    setLimit(e.target.value);
    setCursor(null);
    setPrevStack([]);
  };

  const nextPage = () => {
    if (nextCursor) {
      setPrevStack((s) => [...s, cursor]);
      setCursor(nextCursor);
    }
  };
  const prevPage = () => {
    setPrevStack((s) => {
      if (!s.length) return s;
      const stack = [...s];
      const prev = stack.pop();
      setCursor(prev ?? null);
      return stack;
    });
  };

  return (
    <div className="mx-auto p-6 bg-card text-foreground bg-slate-50 h-full">
      <h1 className="text-3xl font-bold mb-0">Assigner un coach</h1>
      <p className="text-gray-500 text-sm mb-2">
        Voici la liste des coachs disponibles pour assignation
      </p>

      <div className="border rounded-lg p-3 mt-4 bg-white">
        <div className="flex justify-between items-center mb-3">
          <div className="relative w-1/4">
            <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-500" />
            </div>
            <input
              type="search"
              id="default-search"
              className="py-[12px] ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg focus:outline-none w-full"
              placeholder="Rechercher par nom, email,..."
              onChange={handleSearchChange}
              required
            />
          </div>
        </div>

        <TableContainer className="relative overflow-x-auto rounded-lg border border-gray-300">
          {loading ? (
            <div className="flex justify-center my-4">
              <CircularProgress />
            </div>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nom du coach</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Ville</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coaches?.length > 0 ? (
                  coaches?.map((coach) => (
                    <TableRow key={coach?.id}>
                      <TableCell>
                        {coach?.firstName} {coach?.lastName}
                      </TableCell>
                      <TableCell>{coach?.email}</TableCell>
                      <TableCell>
                        {coach?.ville ?? coach?.attributes?.ville ?? "N/A"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          className="no-underline bg-[#0976BC] hover:bg-[#065A94] transition-colors duration-300 capitalize text-white font-normal shadow-none px-[12px] py-[6px] rounded-full text-xs"
                          onClick={() => handleAssignCoach(coach)}
                          disabled={
                            isAssigning &&
                            assigningCoachId === coach.keycloakUserId
                          }
                        >
                          {isAssigning &&
                          assigningCoachId === coach.keycloakUserId
                            ? "Assigning..."
                            : "Assigner"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Aucun coach disponible
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        <div className="flex justify-end items-center p-2 space-x-4">
          <FormControl size="small">
            <Select
              value={limit}
              onChange={changeLimit}
              sx={{ fontSize: 14, minWidth: 60 }}
            >
              {[10, 25, 50].map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton onClick={prevPage} disabled={!prevStack.length}>
            <ChevronLeftIcon className="h-5 w-5" />
          </IconButton>

          <IconButton onClick={nextPage} disabled={!nextCursor}>
            <ChevronRightIcon className="h-5 w-5" />
          </IconButton>
        </div>
        {/* 
        <div className="flex justify-between items-center mt-4">
          <Button onClick={handlePrevPage} disabled={prevCursors.length === 0}>
            Précédent
          </Button>
          <Button onClick={handleNextPage} disabled={!nextCursor}>
            Suivant
          </Button>
        </div> */}
      </div>

      <div className="mt-4">
        <h2 className="text-xl font-semibold">Candidatures sélectionnées</h2>
        <ul className="list-disc pl-5 mt-2">
          {selectedIds.map((id) => (
            <li key={id}>Candidature ID : {id}</li>
          ))}
        </ul>
      </div>

      {successModalOpen && (
        <SuccessModal
          open={successModalOpen}
          onClose={handleCloseSuccessModal}
          coach={selectedCoach}
          message="Surveys successfully assigned to coach."
        />
      )}
    </div>
  );
};

export default AssignCoach;
